import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Logo from "./Logo";
import LiveChatWidget from "./LiveChatWidget";
import QuickInquiry from "./QuickInquiry";
import { List, X, PaperPlaneRight, EnvelopeSimple, MapPin, Phone } from "@phosphor-icons/react";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
];

function Header() {
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    useEffect(() => setOpen(false), [loc.pathname]);

    return (
        <header className="glass-nav sticky top-0 z-50" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
                <Logo />
                <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-4 py-2 rounded-full text-sm font-medium transition-colors font-heading ${
                                    isActive
                                        ? "text-cyan-300 bg-cyan-400/10"
                                        : "text-slate-300 hover:text-cyan-300 hover:bg-slate-800/40"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="hidden md:flex items-center gap-3">
                    <Link to="/get-started" className="btn-primary text-sm" data-testid="header-get-started-btn">
                        Get Started <PaperPlaneRight size={16} weight="bold" />
                    </Link>
                </div>
                <button
                    className="md:hidden p-2 text-slate-200"
                    onClick={() => setOpen(!open)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Menu"
                >
                    {open ? <X size={24} /> : <List size={24} />}
                </button>
            </div>
            {open && (
                <div className="md:hidden border-t border-slate-800 bg-gray-950/95">
                    <div className="px-5 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `px-3 py-2.5 rounded-lg text-sm ${
                                        isActive ? "text-cyan-300 bg-cyan-400/10" : "text-slate-300"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link to="/get-started" className="btn-primary mt-3 justify-center" data-testid="mobile-get-started-btn">
                            Get Started <PaperPlaneRight size={16} weight="bold" />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}

function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const res = await subscribeNewsletter(email);
            toast.success(res.already_subscribed ? "You're already on the list" : "Subscribed — watch for drafting tips.");
            setEmail("");
        } catch {
            toast.error("Unable to subscribe. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="border-t border-slate-800/80 bg-[#050912]" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <Logo size="md" />
                    <p className="text-slate-400 text-sm mt-4 leading-relaxed max-w-sm">
                        Precision land surveying CAD drafting services for engineers, surveyors, and developers across
                        the United States and Canada. 330,000+ drawings delivered. 10+ years of expertise.
                    </p>
                    <div className="mt-6 space-y-2 text-sm text-slate-400">
                        <div className="flex items-center gap-2"><EnvelopeSimple size={16} className="text-cyan-400" /> surveys2cad@gmail.com</div>
                        <div className="flex items-center gap-2"><Phone size={16} className="text-cyan-400" /> Available on request</div>
                        <div className="flex items-center gap-2"><MapPin size={16} className="text-cyan-400" /> Serving USA & Canada — 330k+ drawings delivered</div>
                    </div>
                </div>
                <div className="md:col-span-2">
                    <div className="overline mb-4">Company</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/about" className="text-slate-300 hover:text-cyan-300">About Us</Link></li>
                        <li><Link to="/services" className="text-slate-300 hover:text-cyan-300">Services</Link></li>
                        <li><Link to="/blog" className="text-slate-300 hover:text-cyan-300">Blog</Link></li>
                        <li><Link to="/contact" className="text-slate-300 hover:text-cyan-300">Contact</Link></li>
                        <li><Link to="/get-started" className="text-slate-300 hover:text-cyan-300">Get a Quote</Link></li>
                    </ul>
                </div>
                <div className="md:col-span-3">
                    <div className="overline mb-4">Services</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/services/boundary-survey" className="text-slate-300 hover:text-cyan-300">Boundary Survey</Link></li>
                        <li><Link to="/services/topographic-survey" className="text-slate-300 hover:text-cyan-300">Topographic Survey</Link></li>
                        <li><Link to="/services/alta-nsps-survey" className="text-slate-300 hover:text-cyan-300">ALTA/NSPS Survey</Link></li>
                        <li><Link to="/services/as-built-survey" className="text-slate-300 hover:text-cyan-300">As-Built Survey</Link></li>
                        <li><Link to="/services/subdivision-plat" className="text-slate-300 hover:text-cyan-300">Subdivision Plat</Link></li>
                        <li><Link to="/services/construction-staking" className="text-slate-300 hover:text-cyan-300">Construction Staking</Link></li>
                        <li><Link to="/services/route-corridor-survey" className="text-slate-300 hover:text-cyan-300">Route / Corridor Survey</Link></li>
                        <li><Link to="/services/fema-elevation-certificate" className="text-slate-300 hover:text-cyan-300">FEMA Elevation Certificate</Link></li>
                        <li><Link to="/services/site-plan" className="text-slate-300 hover:text-cyan-300">Site Plan</Link></li>
                        <li><Link to="/services/volumetric-earthwork" className="text-slate-300 hover:text-cyan-300">Volumetric / Earthwork</Link></li>
                        <li><Link to="/services/wetland-delineation" className="text-slate-300 hover:text-cyan-300">Wetland Delineation</Link></li>
                        <li><Link to="/services/3d-laser-scanning" className="text-slate-300 hover:text-cyan-300">3D Laser Scanning</Link></li>
                    </ul>
                </div>
                <div className="md:col-span-3">
                    <div className="overline mb-4">Newsletter</div>
                    <p className="text-sm text-slate-400 mb-3">
                        Drafting tips, standards updates, and case studies. One email a month.
                    </p>
                    <form onSubmit={submit} className="flex flex-col gap-2" data-testid="newsletter-form">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-cyan-500"
                            data-testid="newsletter-email-input"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary text-sm justify-center"
                            data-testid="newsletter-submit-btn"
                        >
                            {loading ? "Subscribing…" : "Subscribe"}
                        </button>
                    </form>
                </div>
            </div>
            <div className="border-t border-slate-800/60">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
                    <div>© {new Date().getFullYear()} Surveys2CAD — surveys2cad.com. All rights reserved.</div>
                    <nav className="flex items-center gap-4" aria-label="Legal">
                        <Link to="/privacy" className="text-slate-400 hover:text-cyan-300" data-testid="footer-privacy-link">Privacy Policy</Link>
                        <span className="text-slate-700">·</span>
                        <Link to="/terms" className="text-slate-400 hover:text-cyan-300" data-testid="footer-terms-link">Terms of Service</Link>
                    </nav>
                    <div className="font-mono-alt">Engineered in the Northern United States</div>
                </div>
            </div>
        </footer>
    );
}

export default function Layout({ children }) {
    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 relative z-[1]">{children}</main>
            <QuickInquiry />
            <Footer />
            <LiveChatWidget />
        </div>
    );
}
